import { useMemo, useState } from 'react'
import {
  DEFAULT_PAGE_SIZE, Card, DataTable, PageHead, Pager, StateBlock, type Column } from '@w7/ui'
import { useViewerCurrency } from '@/api/hooks/shared/useViewerCurrency'
import { count, displayMoney, EMPTY, lll } from '@/lib/format'
import type { CreditLimitTransferRow } from './clTransfersTypes'
import { useCreditLimitTransfers } from './useCreditLimitTransfers'

/**
 * CL Transfers — Winner All Logs "CL Transfers".
 *
 * Credit-limit change history (from / to / new limit / balances / IP), not the
 * Money → Credit transfers give/take statement.
 */

const PAGE_SIZE = DEFAULT_PAGE_SIZE

export function ClTransfersPage() {
  const [page, setPage] = useState(1)
  const currency = useViewerCurrency()
  const { data, isLoading } = useCreditLimitTransfers({ page, pageSize: PAGE_SIZE })

  const columns = useMemo<Column<CreditLimitTransferRow>[]>(
    () => [
      {
        id: 'createdAt',
        header: 'Creation Date',
        cell: (row) => lll(row.createdAt),
      },
      {
        id: 'from',
        header: 'From',
        cell: (row) => row.fromUsername || EMPTY,
      },
      {
        id: 'to',
        header: 'To',
        cell: (row) => row.toUsername || EMPTY,
      },
      {
        id: 'creditLimit',
        header: 'CreditLimit',
        align: 'right',
        numeric: true,
        cell: (row) => displayMoney(row.creditLimit, currency, { round: true }),
      },
      {
        id: 'balance',
        header: 'Balance',
        align: 'right',
        numeric: true,
        cell: (row) => displayMoney(row.balance, currency),
      },
      {
        id: 'oldBalance',
        header: 'Old Balance',
        align: 'right',
        numeric: true,
        cell: (row) => displayMoney(row.oldBalance, currency),
      },
      {
        id: 'ip',
        header: 'IP',
        cell: (row) => row.clientIp || EMPTY,
      },
    ],
    [currency],
  )

  const unavailable = data?.unavailable
  const rows = data?.rows ?? []
  const total = data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE) || 1)

  return (
    <>
      <PageHead
        title="CL Transfers"
        subtitle="Credit-limit changes across the hierarchy — who raised or lowered a line, and the balances around that edit"
      />

      {unavailable ? (
        <div style={{ marginBottom: 'var(--w7-s4)' }}>
          <StateBlock icon="info" title="No data found">
            {unavailable.reason}
            {unavailable.trackedBy ? ` Tracked by ${unavailable.trackedBy}.` : null}
          </StateBlock>
        </div>
      ) : null}

      <Card
        flush
        title="Credit-limit transfers"
        subtitle={
          isLoading
            ? 'Loading'
            : unavailable
              ? 'Source unavailable'
              : `${count(total)} in this list`
        }
        footer={
          unavailable ? null : (
            <Pager
              page={data?.page ?? page}
              pageCount={pageCount}
              onChange={setPage}
              total={total}
              pageSize={PAGE_SIZE}
            />
          )
        }
      >
        <DataTable
          label="Credit-limit transfers"
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          loading={isLoading}
          skeletonRows={8}
          empty={
            unavailable ? (
              <StateBlock icon="sliders" title="Credit-limit log unavailable">
                Live hierarchy did not return credit-limit history. Fixture rows appear only in
                demo mode.
              </StateBlock>
            ) : (
              <StateBlock icon="sliders" title="No credit-limit transfers">
                Raising or lowering an account’s credit limit is recorded here when the log source
                has entries for your seat.
              </StateBlock>
            )
          }
        />
      </Card>
    </>
  )
}
